import {
  IActionConfig,
  useEditorStore,
  useFlowBuilder2ActionValidators,
  ActionTypeNameEnum,
} from '@diana/core';
import { ValidationTip } from '../ValidationTip/ValidationTip';
import { Chip, Stack, Typography, useTheme } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { isEqual } from 'lodash';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { FlowBuilderModalContentTypeEnum } from '../../Store/FlowBuilder2PageStore.types';
import { useMemo } from 'react';
import { useFindActiveTask } from '../../Support/useFindActiveTask';

interface ITaskDrawerActionItemProps {
  action?: IActionConfig;
  isEnabled?: boolean;
  index: number;
}

export const TaskDrawerActionItem = (props: ITaskDrawerActionItemProps) => {
  const { action, isEnabled = true, index } = props;
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();
  const findActiveTask = useFindActiveTask();
  const validators = useFlowBuilder2ActionValidators();

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);
  const setTaskActionIndex = useFlowBuilder2PageStore((s) => s.setTaskActionIndex);

  const getFlow = useEditorStore((s) => s.getFlow);

  const validationErrorResult = useMemo(() => {
    const flow = getFlow();
    const actionSet = findActiveTask(flow);
    if (flow && actionSet && action) {
      for (const validate of Object.values(validators)) {
        const result = validate({ flow, actionSet, action });
        if (result) return result;
      }
    }
    return null;
  }, [action, getFlow, findActiveTask, validators]);

  // const isStopAction = useMemo(() => {
  //   return !action || editorUtils.isStopAction(action);
  // }, [action]);

  const description = useMemo(() => {
    if (!action) return t('descriptions.endThisWork');
    switch (action.executeStatus) {
      case 'Complete':
        if (!action.description || action.description === 'Submit') {
          return t('descriptions.forwardToNextTask');
        }
        break;
      case 'Cancelled':
      case 'Declined':
        if (!action.description || action.description === 'End') {
          return t('descriptions.endThisWork');
        }
        break;
    }
    return action.description;
  }, [t, action]);

  const isEndWork = action?.actionTypeName === ActionTypeNameEnum.Stop || !action;

  const iconSize = 20;
  const iconStyles = { width: iconSize, height: iconSize };
  const iconColor = isEnabled
    ? isEndWork
      ? palette.error.main
      : palette.primary.main
    : palette.text.disabled;

  return (
    <Stack
      onClick={() => {
        if (hasAnyPendingStatus) {
          setActiveModal({ name: FlowBuilderModalContentTypeEnum.AiAcceptReject, context: {} });
          return;
        }
        setTaskActionIndex(index);
      }}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      px={1}
      py={0.75}
      sx={{ border: `1px solid ${palette.border.main}`, borderRadius: 2, cursor: 'pointer' }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            height: 32,
            width: 32,
            flexShrink: 0,
            borderRadius: 1.5,
            border: `1px solid ${
              isEnabled
                ? isEndWork
                  ? palette.error.border
                  : palette.primary.border
                : palette.secondary.border
            }`,
            backgroundColor: isEnabled
              ? isEndWork
                ? palette.error.muted
                : palette.primary.muted
              : palette.secondary.muted,
          }}
        >
          {isEndWork ? (
            <CloseRoundedIcon sx={{ ...iconStyles, color: iconColor }} />
          ) : (
            <ArrowDownwardRoundedIcon sx={{ ...iconStyles, color: iconColor }} />
          )}
        </Stack>
        <Stack>
          <Typography
            fontWeight={500}
            color={isEnabled ? palette.text.primary : palette.text.disabled}
          >
            {action?.displayName || '---'}
          </Typography>
          <Typography
            color={isEnabled ? palette.text.secondary : palette.text.disabled}
            fontSize={12}
          >
            {t('labels.status', { status: description ?? '---' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        {!isEnabled ? (
          <Chip
            label={t('labels.disabled')}
            color="secondary"
            sx={{
              borderRadius: 80,
              backgroundColor: palette.secondary.muted,
              borderColor: palette.secondary.border,
              color: palette.text.secondary,
              '& > .MuiChip-label': { px: 1, fontSize: 11 },
            }}
          />
        ) : null}
        {validationErrorResult ? (
          <ValidationTip
            type={validationErrorResult.type}
            message={validationErrorResult.message}
          />
        ) : null}
        <NavigateNextRoundedIcon sx={iconStyles} />
      </Stack>
    </Stack>
  );
};
